import React from "react"
import styled from "styled-components"
import { graphql } from "gatsby"
import Img from "gatsby-image"
import { useIntl } from "gatsby-plugin-intl"
import PageMetadata from "../../components/PageMetadata"
import PageHero from "../../components/PageHero"
import ButtonLink from "../../components/ButtonLink"
import { Page, Content } from "../../components/SharedStyledComponents"

const Section = styled.div`
  display: flex;
  align-items: center;
  flex-direction: ${(props) => (props.reverse ? "row-reverse" : "row")};
  gap: 4rem;
  margin: 6rem 0;

  @media (max-width: ${(props) => props.theme.breakpoints.l}) {
    flex-direction: column;
    gap: 2rem;
  }
`

const TextContent = styled.div`
  flex: 1;
`

const ImageContainer = styled.div`
  flex: 1.5;
  width: 100%;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 14px 40px rgba(0, 0, 0, 0.2);
  border: 1px solid ${(props) => props.theme.colors.border};
`

const H2 = styled.h2`
  margin-top: 0;
  margin-bottom: 1.5rem;
  font-size: 2rem;
`

const Text = styled.p`
  font-size: 1.25rem;
  line-height: 1.6;
  color: ${(props) => props.theme.colors.text200};
  margin-bottom: 1.5rem;
`

const StepBadge = styled.div`
  display: inline-block;
  background: ${(props) => props.theme.colors.primary};
  color: #fff;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-weight: 600;
  font-size: 0.875rem;
  margin-bottom: 1rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

const CTAContainer = styled.div`
  display: flex;
  justify-content: center;
  margin: 6rem 0;
`

const DevSecOpsAIPage = ({ data }) => {
  const intl = useIntl()

  const heroContent = {
    title: "DEVSECOPS MONITOR AI",
    header: "Tu Asistente Inteligente",
    subtitle:
      "Interactúa con toda la infraestructura y métricas de tu organización mediante lenguaje natural. Analiza despliegues, repositorios y fábricas de software en tiempo real.",
    image: data.hero.childImageSharp.fluid,
    alt: "DevSecOps Monitor AI Chatbot",
    buttons: [
      {
        content: "Acceder al Asistente",
        path: "https://devsecops-monitor-ai.newinntech.com/",
      },
    ],
  }

  return (
    <Page>
      <PageMetadata
        title="DevSecOps Monitor AI"
        description="Asistente inteligente para la monitorización de infraestructura DevSecOps y fábricas de software."
      />
      <PageHero content={heroContent} />

      <Content>
        {/* Step 1 */}
        <Section>
          <TextContent>
            <StepBadge>Funcionalidad</StepBadge>
            <H2>Interfaz Conversacional Inteligente</H2>
            <Text>
              Comunícate con tu infraestructura como si hablaras con un miembro
              más de tu equipo. El asistente impulsado por modelos avanzados
              (como Gemini 2.5 Flash) te permite solicitar informes DevOps,
              revisar métricas CI/CD, analizar repositorios fallidos y consultar
              la tasa de éxito de tus despliegues al instante.
            </Text>
          </TextContent>
          <ImageContainer>
            <Img
              fluid={data.screen1.childImageSharp.fluid}
              alt="Chatbot Interface"
            />
          </ImageContainer>
        </Section>

        {/* Step 2 */}
        <Section reverse>
          <TextContent>
            <StepBadge>Analítica</StepBadge>
            <H2>Filtros de Tiempo y Ojo de Águila</H2>
            <Text>
              La Inteligencia Artificial es capaz de traducir fechas relativas y
              rangos exactos. Realiza preguntas complejas como la tasa de éxito
              durante semestres pasados, o aprovecha la vista "Ojo de Águila"
              para obtener análisis a nivel multitenant comparando el
              rendimiento entre todos los clientes de tu plataforma.
            </Text>
          </TextContent>
          <ImageContainer>
            <Img
              fluid={data.screen2.childImageSharp.fluid}
              alt="Sugerencias Avanzadas"
            />
          </ImageContainer>
        </Section>

        {/* Step 3 */}
        <Section>
          <TextContent>
            <StepBadge>Gestión</StepBadge>
            <H2>Gestión de Cuentas y Estadísticas FDS</H2>
            <Text>
              Navega rápidamente a través de las Sugerencias de Ayuda
              predefinidas. Extrae resúmenes gerenciales sobre el estado
              consolidado de la infraestructura, audita tus Workspaces y obtén
              informes detallados del desempeño de cada Fábrica de Software
              (FDS) y de proyectos individuales con un solo clic.
            </Text>
          </TextContent>
          <ImageContainer>
            <Img
              fluid={data.screen3.childImageSharp.fluid}
              alt="Sugerencias de Ayuda"
            />
          </ImageContainer>
        </Section>

        <CTAContainer>
          <ButtonLink
            to="https://devsecops-monitor-ai.newinntech.com/"
            size="large"
          >
            Comenzar a Chatear
          </ButtonLink>
        </CTAContainer>
      </Content>
    </Page>
  )
}

export default DevSecOpsAIPage

export const query = graphql`
  query {
    hero: file(relativePath: { eq: "devsecops_ai_hero.png" }) {
      childImageSharp {
        fluid(maxWidth: 800) {
          ...GatsbyImageSharpFluid
        }
      }
    }
    screen1: file(relativePath: { eq: "devsecops_ai_screen_1.png" }) {
      childImageSharp {
        fluid(maxWidth: 1200) {
          ...GatsbyImageSharpFluid
        }
      }
    }
    screen2: file(relativePath: { eq: "devsecops_ai_screen_2.png" }) {
      childImageSharp {
        fluid(maxWidth: 1200) {
          ...GatsbyImageSharpFluid
        }
      }
    }
    screen3: file(relativePath: { eq: "devsecops_ai_screen_3.png" }) {
      childImageSharp {
        fluid(maxWidth: 1200) {
          ...GatsbyImageSharpFluid
        }
      }
    }
  }
`
